import React from "react";
import { ChevronRight, Info } from "lucide-react";
import CommonAvatar from "@/shared/components/ui/Avatar/CommonAvatar";
import CustomCommonTooltip from "@/shared/components/ui/Upload/ToolTip/CustomCommonTooltip";
import BorderBottomCard from "../../BorderBottomCard";
import {
  companyDomainHref,
  sanitizeCompanyLogoUrl,
} from "@/features/dashboard/utils/accountsDashboardUtils";
import { getCleanName } from "@/shared/utils/accountConfig";
import SimilarAccountActionButton from "./SimilarAccountActionButton";

const SimilarAccountCard = ({ account }) => {
  const href = companyDomainHref(account?.company_domain);
  const name = getCleanName(account?.company_name || "—");
  const referenceName = account?.reference_account_name || account?.matched_account;
  const referenceScore = Number(account?.reference_score);
  const openCompany = () => {
    if (href) window.open(href, "_blank", "noopener,noreferrer");
  };

  return (
    <BorderBottomCard
      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-colors"
      role={href ? "link" : undefined}
      tabIndex={href ? 0 : undefined}
      onClick={openCompany}
      onKeyDown={(event) => {
        if (href && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          openCompany();
        }
      }}
    >
      <div className="flex items-center gap-2 w-full min-w-0">
        <CommonAvatar
          imageUrl={sanitizeCompanyLogoUrl(account?.company_logo)}
          name={name}
          className="rounded-full object-cover min-w-8 min-h-8 w-8 h-8 shrink-0"
          size="md"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h5 className="text-gray-800 dark:text-gray-100 text-[0.75rem] leading-5 font-medium truncate">
              {name}
            </h5>
            {account?.reason ? (
              <CustomCommonTooltip content={account.reason}>
                <Info size={12} className="text-gray-400 dark:text-gray-500" aria-label="Why this account" />
              </CustomCommonTooltip>
            ) : null}
          </div>
          <p className="text-gray-500 dark:text-gray-400 text-[0.69rem] truncate">
            {[account?.location, account?.company_size].filter(Boolean).join(" · ") ||
              account?.company_domain}
          </p>
          {referenceName ? (
            <p className="text-gray-400 dark:text-gray-500 text-[0.62rem] truncate">
              Based on: {referenceName}
              {Number.isFinite(referenceScore)
                ? ` · Score: ${referenceScore.toFixed(2)}`
                : ""}
            </p>
          ) : null}
        </div>
        <SimilarAccountActionButton account={account} />
        <ChevronRight size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
      </div>
    </BorderBottomCard>
  );
};

export default SimilarAccountCard;
